import { Toaster, toast, type Toast } from 'react-hot-toast';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastPayload {
  title: string;
  description?: string;
  duration?: number;
}

// ─── Estilos por variante (alineados con el design system del frontend) ───────

const variantConfig: Record<
  ToastVariant,
  { icon: React.ReactNode; accentColor: string; bgColor: string }
> = {
  success: {
    icon: <CheckCircle size={17} strokeWidth={2} />,
    accentColor: '#22c55e',
    bgColor: 'rgba(34,197,94,0.08)',
  },
  error: {
    icon: <XCircle size={17} strokeWidth={2} />,
    accentColor: 'rgb(var(--brand-primary))',
    bgColor: 'rgba(var(--brand-primary), 0.06)',
  },
  warning: {
    icon: <AlertTriangle size={17} strokeWidth={2} />,
    accentColor: '#f59e0b',
    bgColor: 'rgba(245,158,11,0.08)',
  },
  info: {
    icon: <Info size={17} strokeWidth={2} />,
    accentColor: 'rgb(var(--brand-secondary))',
    bgColor: 'rgba(var(--brand-secondary), 0.08)',
  },
};

// ─── Renderizador del toast personalizado ────────────────────────────────────

function renderToast(
  t: Toast,
  variant: ToastVariant,
  title: string,
  description?: string
) {
  const { icon, accentColor, bgColor } = variantConfig[variant];

  return (
    <div
      style={{
        background: bgColor,
        border: `1px solid ${accentColor}30`,
        minWidth: 270,
        maxWidth: 360,
      }}
      className={`
        flex items-start gap-3 rounded-2xl shadow-xl px-4 py-3 w-full
        backdrop-blur-md transition-all duration-300
        ${t.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
      `}
    >
      {/* Ícono */}
      <span className="flex-shrink-0 mt-0.5" style={{ color: accentColor }}>
        {icon}
      </span>

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold leading-snug"
          style={{ color: 'rgb(var(--brand-text))' }}
        >
          {title}
        </p>
        {description && (
          <p
            className="text-xs mt-0.5 leading-relaxed"
            style={{ color: 'rgb(var(--brand-text-secondary))' }}
          >
            {description}
          </p>
        )}
      </div>

      {/* Botón cerrar */}
      <button
        onClick={() => toast.dismiss(t.id)}
        className="flex-shrink-0 mt-0.5 rounded-md p-0.5 opacity-50 hover:opacity-100 transition-opacity duration-150"
        style={{ color: 'rgb(var(--brand-text))' }}
        aria-label="Cerrar notificación"
      >
        <X size={13} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ─── Hook público ─────────────────────────────────────────────────────────────

/**
 * `useToast` — hook para disparar toasts informativos desde cualquier componente.
 *
 * @example
 * const { success, error, warning, info } = useToast();
 * success({ title: '¡Agregado!', description: 'El producto fue añadido al carrito.' });
 */
export function useToast() {
  const show = (variant: ToastVariant, payload: ToastPayload) => {
    const { title, description, duration = 4000 } = payload;

    toast.custom(
      (t) => renderToast(t, variant, title, description),
      { duration, position: 'bottom-right' }
    );
  };

  return {
    success: (payload: ToastPayload) => show('success', payload),
    error:   (payload: ToastPayload) => show('error',   payload),
    warning: (payload: ToastPayload) => show('warning', payload),
    info:    (payload: ToastPayload) => show('info',    payload),
  };
}

// ─── Provider (colocar una sola vez en el layout raíz) ───────────────────────

/**
 * `ToastProvider` — monta el contenedor de `react-hot-toast`.
 * Colocarlo una vez en `App.tsx` o en el layout principal.
 *
 * @example
 * <ToastProvider />
 */
export function ToastProvider() {
  return (
    <Toaster
      position="bottom-right"
      gutter={10}
      containerStyle={{ bottom: 24, right: 24 }}
      toastOptions={{
        duration: 4000,
        style: { padding: 0, background: 'transparent', boxShadow: 'none' },
      }}
    />
  );
}
